import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { isTauri } from '@/lib/utils/tauri'

/**
 * Two-finger trackpad swipe to go back/forward, the way Safari and Chrome do it.
 *
 * The webview does not give us the native gesture (wry leaves
 * `allowsBackForwardNavigationGestures` off, and turning it on would drive the
 * webview's own history rather than the router), so the gesture is
 * reconstructed from the horizontal `wheel` stream a trackpad produces:
 *
 * - a gesture is claimed only when the wheel stream is clearly horizontal and
 *   no ancestor scroller can still absorb it in that direction — same
 *   precedence rule the browsers use, so swiping a wide table scrolls the
 *   table instead of navigating;
 * - an edge bubble tracks the pull the whole time the fingers are down, and
 *   the pull can be dragged back to the origin (or reversed into the opposite
 *   direction) to call the whole thing off;
 * - nothing commits mid-pull. Navigation happens on release, and only if the
 *   pull ended past the threshold — the arm state of the bubble is the
 *   promise. Release is inferred from the wheel stream going quiet, which is
 *   the only signal the webview gives us; trackpad momentum after the lift
 *   still counts toward the pull, so a flick completes the way it does in
 *   Chrome.
 *
 * Desktop only — the hook is a no-op outside Tauri, where the real gesture is
 * already handled by the browser.
 */

/** Accumulated horizontal distance (px) that commits the navigation. */
const COMMIT_DISTANCE = 90
/** Horizontal dominance required before a wheel stream counts as a swipe. */
const DIRECTION_RATIO = 1.5
/** Minimum first-event delta before we consider claiming the stream. */
const CLAIM_DELTA = 2
/** Silence (ms) after the last wheel event that counts as the fingers lifting. */
const GESTURE_END_MS = 150
/** Bubble travel between fully hidden and fully revealed. */
const HIDDEN_OFFSET = 56
const SHOWN_OFFSET = 14

type SwipeDirection = -1 | 1

const CHEVRON_LEFT = 'M14.5 5.5 8.5 12l6 6.5'
const CHEVRON_RIGHT = 'M9.5 5.5 15.5 12l-6 6.5'

/**
 * Walks up from the wheel target looking for a scroller that still has room to
 * move in the swipe direction (or an explicit `data-no-swipe-nav` opt-out).
 * Anything it finds owns the gesture, not us.
 */
function isGestureAbsorbed(
  target: EventTarget | null,
  direction: SwipeDirection,
): boolean {
  let node: Element | null =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null

  while (node) {
    if (node.hasAttribute('data-no-swipe-nav')) return true

    const overflowX = getComputedStyle(node).overflowX
    if (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') {
      const maxScroll = node.scrollWidth - node.clientWidth
      if (maxScroll > 1) {
        // Back (deltaX < 0) consumes scrollLeft, forward adds to it.
        const room =
          direction === -1 ? node.scrollLeft : maxScroll - node.scrollLeft
        if (room > 1) return true
      }
    }

    node = node.parentElement
  }

  return false
}

interface Indicator {
  update: (direction: SwipeDirection, progress: number) => void
  /** `committed` fades the bubble out in place; otherwise it slides back off. */
  release: (committed: boolean) => void
  destroy: () => void
}

function createIndicator(): Indicator {
  const root = document.createElement('div')
  root.className = 'swipe-nav-indicator'
  root.setAttribute('aria-hidden', 'true')
  root.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">' +
    `<path d="${CHEVRON_LEFT}" /></svg>`
  document.body.appendChild(root)

  const path = root.querySelector('path') as SVGPathElement

  const place = (direction: SwipeDirection, progress: number) => {
    const armed = progress >= 1
    const offset = -HIDDEN_OFFSET + progress * (HIDDEN_OFFSET + SHOWN_OFFSET)
    root.style.opacity = String(Math.min(1, progress * 1.7))
    root.style.transform =
      `translateX(${direction === -1 ? offset : -offset}px)` +
      ` translateY(-50%) scale(${armed ? 1.08 : 1})`
    root.dataset.armed = armed ? 'true' : 'false'
  }

  return {
    update: (direction, progress) => {
      root.dataset.side = direction === -1 ? 'left' : 'right'
      root.dataset.dragging = 'true'
      path.setAttribute('d', direction === -1 ? CHEVRON_LEFT : CHEVRON_RIGHT)
      place(direction, progress)
    },
    release: (committed: boolean) => {
      const direction: SwipeDirection = root.dataset.side === 'right' ? 1 : -1
      root.dataset.dragging = 'false'
      if (committed) {
        root.style.opacity = '0'
        return
      }
      place(direction, 0)
    },
    destroy: () => root.remove(),
  }
}

export function useSwipeNavigation(): void {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  // React Router stamps its session-history index onto history.state, which is
  // what tells us whether there is anywhere to swipe to. Only a PUSH drops the
  // forward entries, so that is the one case where the high-water mark resets;
  // a REPLACE leaves them in place.
  const furthestIndexRef = useRef(0)

  useEffect(() => {
    const index = (window.history.state as { idx?: number } | null)?.idx
    if (typeof index !== 'number') return

    furthestIndexRef.current =
      navigationType === 'PUSH'
        ? index
        : Math.max(furthestIndexRef.current, index)
  }, [location, navigationType])

  useEffect(() => {
    if (!isTauri()) return

    const indicator = createIndicator()

    let active = false
    let claimed = false
    let direction: SwipeDirection = -1
    let accumulatedX = 0
    let accumulatedY = 0
    let endTimer: ReturnType<typeof setTimeout> | undefined

    const canNavigate = (towards: SwipeDirection) => {
      const index = (window.history.state as { idx?: number } | null)?.idx
      if (typeof index !== 'number') {
        return towards === -1 ? window.history.length > 1 : false
      }
      return towards === -1 ? index > 0 : index < furthestIndexRef.current
    }

    /**
     * The wheel stream has gone quiet, so the fingers are off the trackpad and
     * any momentum has died. This is the only place navigation happens.
     */
    const endGesture = () => {
      const committed = claimed && accumulatedX * direction >= COMMIT_DISTANCE
      const wasClaimed = claimed
      const settledDirection = direction

      active = false
      claimed = false
      accumulatedX = 0
      accumulatedY = 0

      if (wasClaimed) indicator.release(committed)
      if (committed) navigate(settledDirection)
    }

    const handleWheel = (event: WheelEvent) => {
      // Line/page deltas come from classic mouse wheels; ctrl+wheel is a pinch.
      if (event.deltaMode !== 0 || event.ctrlKey) return

      if (endTimer) clearTimeout(endTimer)
      endTimer = setTimeout(endGesture, GESTURE_END_MS)

      if (!active) {
        const horizontal = Math.abs(event.deltaX)
        if (
          horizontal < CLAIM_DELTA ||
          horizontal < Math.abs(event.deltaY) * DIRECTION_RATIO
        ) {
          // Not a horizontal stream — let the silence timer clear it out.
          return
        }

        active = true
        direction = event.deltaX < 0 ? -1 : 1
        claimed =
          canNavigate(direction) && !isGestureAbsorbed(event.target, direction)
      }

      if (!claimed) return

      accumulatedX += event.deltaX
      accumulatedY += event.deltaY

      // A stream that turns vertical mid-swipe was a diagonal scroll, not a
      // navigation gesture.
      if (Math.abs(accumulatedY) > Math.abs(accumulatedX) * 1.2) {
        claimed = false
        indicator.release(false)
        return
      }

      // Dragged back through the origin: either the user is reversing into the
      // opposite navigation, or they are pulling out of the gesture entirely.
      // Either way nothing has been committed yet, so both are still live.
      if (accumulatedX * direction < 0) {
        const reversed: SwipeDirection = direction === -1 ? 1 : -1
        if (
          Math.abs(accumulatedX) > CLAIM_DELTA &&
          canNavigate(reversed) &&
          !isGestureAbsorbed(event.target, reversed)
        ) {
          direction = reversed
        } else {
          accumulatedX = 0
        }
      }

      const pulled = Math.max(0, accumulatedX * direction)
      indicator.update(direction, Math.min(1, pulled / COMMIT_DISTANCE))
    }

    window.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      window.removeEventListener('wheel', handleWheel)
      if (endTimer) clearTimeout(endTimer)
      indicator.destroy()
    }
  }, [navigate])
}
